import { onUnmounted, ref, unref } from 'vue'

let loadPromise = null

function loadGoogleMaps(apiKey) {
  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key is missing'))
  }

  if (window.google?.maps?.places) {
    return Promise.resolve(window.google.maps)
  }

  if (loadPromise) {
    return loadPromise
  }

  loadPromise = new Promise((resolve, reject) => {
    const callbackName = '__registerPlacesInit'

    window[callbackName] = () => {
      resolve(window.google.maps)
    }

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&callback=${callbackName}`
    script.async = true
    script.defer = true
    script.onerror = () => reject(new Error('Failed to load Google Maps'))
    document.head.appendChild(script)
  })

  return loadPromise
}

function mapPlaceToForm(place, fallbackAddress) {
  const mapped = {
    practice_address: place.formatted_address ?? fallbackAddress,
    city: '',
    state: '',
    country: '',
    zip_code: '',
  }

  const componentForm = {
    locality: { field: 'city', nameType: 'long_name' },
    postal_town: { field: 'city', nameType: 'long_name' },
    administrative_area_level_2: { field: 'city', nameType: 'long_name' },
    administrative_area_level_1: { field: 'state', nameType: 'short_name' },
    country: { field: 'country', nameType: 'long_name' },
    postal_code: { field: 'zip_code', nameType: 'short_name' },
  }

  for (const component of place.address_components ?? []) {
    for (const type of component.types) {
      const rule = componentForm[type]
      if (!rule || mapped[rule.field]) {
        continue
      }

      mapped[rule.field] = component[rule.nameType] ?? ''
    }
  }

  return mapped
}

export function useGooglePlaces(apiKeySource) {
  const isReady = ref(false)
  const instances = []

  const initAutocomplete = async (inputEl, onPlaceSelected, { addressOnly = false } = {}) => {
    const apiKey = unref(apiKeySource)
    if (!inputEl || !apiKey) return

    await loadGoogleMaps(apiKey)
    isReady.value = true

    const autocomplete = new window.google.maps.places.Autocomplete(inputEl, {
      types: ['address'],
      fields: ['address_components', 'formatted_address'],
    })

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace()
      if (!place?.address_components?.length) return

      if (addressOnly) {
        onPlaceSelected({
          practice_address_additional: place.formatted_address ?? inputEl.value,
        })
        return
      }

      onPlaceSelected(mapPlaceToForm(place, inputEl.value))
    })

    instances.push(autocomplete)
  }

  onUnmounted(() => {
    instances.length = 0
  })

  return { isReady, initAutocomplete }
}
